// Link AML — the identity-verification half of linking, as its own step.
//
// BM-H382 (7 September 2026).
//
// Onboarding and AML are two separate compliance steps with two separate
// evidence trails. Until now the AML half could only be performed as a
// side effect of Link OB, which meant a fee earner could not inherit a
// Thirdfort result without also linking onboarding, and could not point
// the two halves at different earlier matters. A client's onboarding
// pack may sit on one file while their identity check was run on
// another, and the system has to be able to say so.
//
// Same trust-context rules as Link OB, because they are properties of
// linking rather than of onboarding:
//   - The current matter ref is shown read-only and is never mutated by
//     link or unlink. The UI re-asserts it against the response.
//   - Search uses /api/smart-search with matters_only and active_only=false.
//   - The backend refuses in both directions: nothing is inherited unless
//     the source matter's AML is genuinely satisfied, and an AML result
//     performed on this matter is never overwritten. A refusal is shown
//     to the operator in full rather than swallowed.
//
// BM-H504 (11 September 2026) — the identity near miss now has a way out.
//
// On BL.VEC.006.1507 the refusal was "the AML on the source matter was
// performed on Benjamin Raphael Davis … but this matter requires Ben
// Davis … those are different people". That refusal is correct and stays
// (Vanessa's BM-H283 ruling: an AML is against a natural person, and a
// near miss is a mismatch a human decides). What did not exist was the
// human deciding: staff read that message, knew perfectly well it was one
// man, and had nothing to press.
//
// So the refusal panel now offers a SEPARATE confirmation step for that
// one case, and only that one case — the backend says whether it is
// available (`aml_subject_override_available`), and an expired, missing or
// unnamed-source refusal never is. It is deliberately not a second press
// of the Link AML button: it is its own panel, with the two names spelled
// out, its own tick confirming they are the same natural person, its own
// written reason, and its own submit. A director or the MLCO/supervisor
// may use it; anyone else is refused by the backend with a 403 that is
// shown here in full.
//
// BM-H573 (21 September 2026) — the matter you need is offered, not typed.
//
// Liezel, on three ILA matters (BL.VEC.002.1412 Reeken Rajnikant Patel,
// BL.VEC.002.1025 Jaswinder Sohi, BL.VEC.002.1023 Aida Gremi): "I tried to
// link the AML but the RESI matter where I should link it is not showing."
//
// Two reasons, both here rather than in the compliance logic. BM-H396 built
// the candidate finder on 8 September and wired it into Link OB only, so this
// dialog still opened on an empty box and depended on the operator knowing a
// reference. And its free-text search fired with no abort and no sequence
// check, across three collections plus a SharePoint Graph call, so a slower
// earlier query could land its results underneath the reference just typed —
// which is what the 21 September screenshot shows.
//
// So: the client's other matters are fetched on mount, ordered AML-first, with
// the completion date, the validity window and the refusal reason on each one;
// the matters that were found but cannot be linked as they stand are shown
// WITH the reason rather than counted invisibly; and a search response that is
// not for the current query is dropped.
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";

const getHeaders = () => {
  const uid =
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") ||
    "";
  const email =
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") ||
    "";
  const h = {};
  if (uid) h["X-Bonsai-User-Id"] = uid;
  if (email) h["X-User-Email"] = email;
  return h;
};

// ─── Modal ───────────────────────────────────────────────────────────────────
export function LinkAMLModal({ matterRef, onClose, onLinked, onGoToForf }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [success, setSuccess] = useState(null);
  // BM-H504 — the identity-override step. Its own state, so nothing about
  // the ordinary link flow can carry a confirmation into a later attempt.
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideConfirmed, setOverrideConfirmed] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideErr, setOverrideErr] = useState(null);
  // BM-H573 — the client's other matters, found for the operator rather than
  // remembered by them.
  const [suggestions, setSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const debounceRef = useRef(null);

  const currentRefAtMount = useRef(matterRef);

  // AML first: this dialog exists to carry an identity check across, so a
  // candidate that would do that outranks one that only satisfies onboarding,
  // whatever the backend's OB-weighted order put first.
  const amlFirst = (list) =>
    [...(list || [])].sort(
      (a, b) =>
        (b.would_carry_aml === true ? 1 : 0) - (a.would_carry_aml === true ? 1 : 0),
    );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/matters/${encodeURIComponent(matterRef)}/link-candidates`,
          { headers: getHeaders() },
        );
        if (cancelled) return;
        setSuggestions(res.data || null);
        // Pre-select only a file that would actually carry the AML, and only
        // when there is exactly one. Two equally good files is a question for
        // a human, and a silent pick on an identity control would be worse
        // than the typing this replaces.
        const carriers = (res.data?.candidates || []).filter(
          (c) => c.would_carry_aml === true,
        );
        if (carriers.length === 1) setSelected(carriers[0]);
      } catch (e) {
        // A failed lookup must never block the manual path that has always
        // worked. Say so quietly and leave the search box.
        if (!cancelled) setSuggestions({ lookup_failed: true });
      } finally {
        if (!cancelled) setLoadingSuggestions(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [matterRef]);

  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // BM-H573: one in-flight search at a time, and a response belonging to a
    // query the operator has already replaced is discarded rather than
    // rendered underneath the new one.
    const controller = new AbortController();
    let stale = false;
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      setErr(null);
      try {
        const res = await axios.get(
          `${API}/smart-search`,
          {
            params: { q: query, limit: 10, active_only: false, matters_only: true },
            headers: getHeaders(),
            signal: controller.signal,
          },
        );
        // The backend echoes the query it answered. Trust that over arrival
        // order, and fall back to the local staleness flag when it is absent.
        if (stale) return;
        if (res.data?.query !== undefined && res.data.query !== query) return;
        const items = res.data?.results || res.data?.matters || [];
        setResults(items.filter((m) => {
          const ref = m.matter_reference || m.matter_ref;
          return ref && ref !== matterRef;
        }));
      } catch (e) {
        if (stale || e?.code === "ERR_CANCELED" || e?.name === "CanceledError"
            || e?.name === "AbortError") return;
        setErr(errorMessage(e, "Search failed — try a more specific query"));
      } finally {
        if (!stale) setSearching(false);
      }
    }, 250);
    return () => {
      stale = true;
      try { controller.abort(); } catch (e) {}
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, matterRef]);

  const handleLink = async (override) => {
    if (!selected) return;
    setBusy(true);
    setErr(null);
    setOverrideErr(null);
    if (!override) setSuccess(null);
    const payload = {
      linked_matter_reference: selected.matter_reference || selected.matter_ref,
    };
    if (override) {
      // All three, always together. The backend refuses the flag on its
      // own, and it should: this is an identity control.
      payload.override_subject_mismatch = true;
      payload.confirm_same_natural_person = true;
      payload.override_reason = overrideReason.trim();
    }
    try {
      const res = await axios.post(
        `${API}/matters/${encodeURIComponent(matterRef)}/link-aml`,
        payload,
        { headers: getHeaders() },
      );
      if (res.data?.matter_reference !== currentRefAtMount.current) {
        setErr(
          `Refusing to apply — server returned matter_reference=${res.data?.matter_reference} `
          + `but we asked to link ${currentRefAtMount.current}. Aborting and not refreshing.`,
        );
        return;
      }
      const inh = res.data?.aml_inheritance || {};
      setSuccess({
        linked: res.data.linked_aml_matter_reference,
        inherited: inh.aml_inherited === true,
        reason: inh.reason,
        completedAt: inh.original_completed_at,
        expiresAt: inh.aml_expires_at,
        stale: inh.stale === true,
        reportStatus: inh.thirdfort_report_pdf_copy_status,
        reportError: inh.report_copy_error,
        filesCopied: inh.report_files_copied || [],
        // BM-H504 — was this refusal the one a human may confirm, and if
        // the result did come across, did it come across on somebody's
        // confirmation rather than on a match?
        overrideAvailable: inh.aml_subject_override_available === true,
        sourceSubject:
          inh.aml_subject_source_description || inh.aml_subject_source_name || "",
        targetSubject:
          inh.aml_subject_target_description || inh.aml_subject_target_name || "",
        overridden: inh.aml_subject_mismatch_overridden === true,
        overriddenBy: inh.aml_subject_mismatch_overridden_by,
      });
      if (override) {
        setOverrideOpen(false);
        setOverrideConfirmed(false);
        setOverrideReason("");
      }
      // BM-H525: tell any other mounted page (e.g. MatterDetail.jsx, a
      // different route from this modal's PipelineTrackerCore host) that
      // AML state changed, so its gate badges refetch on next window focus
      // instead of showing the stale pre-link/pre-override state. Same
      // flag + style RefFixerPage/ClientFixerPage already write.
      try { sessionStorage.setItem("bonsai_pipeline_cache_invalidated_at", String(Date.now())); } catch (e) {}
      if (onLinked) onLinked(res.data);
    } catch (e) {
      const message = errorMessage(e, "Link AML failed");
      if (override) setOverrideErr(message);
      else setErr(message);
    } finally {
      setBusy(false);
    }
  };

  // The same 20-character floor the backend applies, so the button is not
  // enabled into a refusal. The backend remains the authority.
  const reasonLongEnough = overrideReason.trim().length >= 20;
  const canSubmitOverride = overrideConfirmed && reasonLongEnough && !busy;

  return (
    <div data-testid="link-aml-modal-overlay" style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, color: "var(--green)" }}>Link AML</h3>
          <button onClick={onClose} aria-label="Close" style={iconBtnStyle}>×</button>
        </div>

        <div style={{ marginTop: 6, fontSize: 11, color: "var(--text-muted)" }}>
          Inherit a completed AML / Thirdfort result onto{" "}
          <strong data-testid="link-aml-current-ref">{matterRef}</strong> from another
          matter for the same client. The Thirdfort report is copied into this
          matter's own folder, and the original completion date is carried
          across so inherited evidence is never shown as fresh work. This
          matter's reference is <strong>never</strong> overwritten.
        </div>

        {!success && (
          <>
            {/* BM-H573: what the system already knows, before anyone types a
                reference from memory. AML-first, because that is the control
                this dialog is for. */}
            {loadingSuggestions && (
              <div style={hintStyle} data-testid="link-aml-candidates-loading">
                Looking for this client's other matters…
              </div>
            )}

            {!loadingSuggestions && suggestions?.candidates?.length > 0 && (
              <div style={{ marginTop: 12 }} data-testid="link-aml-candidates">
                <label style={labelStyle}>
                  This client's other matters
                  {suggestions.person ? ` — ${suggestions.person}` : ""}
                </label>
                {suggestions.ambiguous && (
                  <div
                    style={{ ...hintStyle, color: "#fbbf24" }}
                    data-testid="link-aml-candidates-ambiguous"
                  >
                    More than one file would do the same job here, so none has
                    been pre-selected. Choose the right one.
                  </div>
                )}
                {amlFirst(suggestions.candidates).map((c) => {
                  const isSel =
                    selected &&
                    (selected.matter_reference || selected.matter_ref) ===
                      c.matter_reference;
                  return (
                    <button
                      key={c.matter_reference}
                      data-testid={`link-aml-candidate-${c.matter_reference}`}
                      onClick={() => setSelected(c)}
                      style={{
                        ...resultRowStyle,
                        background: isSel
                          ? "rgba(26, 171, 110, 0.18)"
                          : "transparent",
                        borderColor: isSel ? "var(--green)" : "var(--navy-border)",
                      }}
                    >
                      <div style={{ fontWeight: 700, color: "var(--text)" }}>
                        {c.matter_reference}
                        {c.would_carry_aml === true && (
                          <span
                            data-testid={`link-aml-candidate-carries-${c.matter_reference}`}
                            style={{
                              marginLeft: 8,
                              fontSize: 10,
                              fontWeight: 700,
                              color: "var(--green)",
                            }}
                          >
                            CARRIES AML
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                        {c.client_name || c.person || "(no client name)"}
                        {c.matter_type ? ` · ${c.matter_type}` : ""}
                      </div>
                      {/* Whose check it is matters more here than whose file
                          it is: on a joint matter they are not the same
                          person. */}
                      {c.person && c.person !== c.client_name && (
                        <div
                          style={{ fontSize: 10, color: "var(--text-muted)" }}
                          data-testid={`link-aml-candidate-person-${c.matter_reference}`}
                        >
                          Identity check held on {c.person}
                        </div>
                      )}
                      <div
                        style={{ fontSize: 11, color: "var(--text)", marginTop: 3 }}
                        data-testid={`link-aml-candidate-summary-${c.matter_reference}`}
                      >
                        {c.summary}
                      </div>
                      {(c.aml_completed_at || c.aml_expires_at) && (
                        <div
                          style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}
                          data-testid={`link-aml-candidate-dates-${c.matter_reference}`}
                        >
                          {c.aml_completed_at
                            ? `Completed ${String(c.aml_completed_at).slice(0, 10)}`
                            : ""}
                          {c.aml_expires_at
                            ? `${c.aml_completed_at ? " · " : ""}valid to ${String(c.aml_expires_at).slice(0, 10)}`
                            : ""}
                        </div>
                      )}
                      {c.aml_reason && (
                        <div
                          style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}
                          data-testid={`link-aml-candidate-reason-${c.matter_reference}`}
                        >
                          {c.aml_reason}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* BM-H573: found, and not offerable as it stands. Previously this
                was a number nobody saw, which is how "the RESI matter is not
                showing" became indistinguishable from "there is no such
                matter". These rows are deliberately not clickable. */}
            {!loadingSuggestions && suggestions?.not_offered?.length > 0 && (
              <div style={{ marginTop: 12 }} data-testid="link-aml-not-offered">
                <label style={labelStyle}>
                  Found, but cannot be linked as it stands
                </label>
                {suggestions.not_offered.map((n) => (
                  <div
                    key={n.matter_reference}
                    data-testid={`link-aml-not-offered-${n.matter_reference}`}
                    style={{
                      ...resultRowStyle,
                      cursor: "default",
                      background: "transparent",
                      borderColor: "var(--navy-border)",
                      opacity: 0.85,
                    }}
                  >
                    <div style={{ fontWeight: 700, color: "var(--text-muted)" }}>
                      {n.matter_reference}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                      {n.client_name || "(no client name)"}
                      {n.matter_type ? ` · ${n.matter_type}` : ""}
                    </div>
                    {n.people?.length > 0 && (
                      <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>
                        People on that matter: {n.people.join("; ")}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: "#fbbf24", marginTop: 3 }}>
                      {n.reason}
                    </div>
                  </div>
                ))}
                <div style={hintStyle}>
                  If one of these really is the same person written two ways,
                  select it by reference below and use the same-person
                  confirmation. Otherwise send a fresh Thirdfort.
                </div>
              </div>
            )}

            {!loadingSuggestions &&
              suggestions &&
              !suggestions.lookup_failed &&
              !(suggestions.candidates?.length > 0) &&
              !(suggestions.not_offered?.length > 0) && (
                <div style={hintStyle} data-testid="link-aml-candidates-none">
                  {suggestions.reason ||
                    "No other matters were found for this client."}
                </div>
              )}

            <div style={{ marginTop: 12 }}>
              <label style={labelStyle} htmlFor="link-aml-search">
                Search by matter reference or client name
              </label>
              <input
                id="link-aml-search"
                data-testid="link-aml-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g. BL.VEC.006.0528 or Davis"
                style={inputStyle}
                autoFocus
              />
            </div>

            <div style={resultListStyle}>
              {searching && <div style={hintStyle}>Searching…</div>}
              {!searching && query.length >= 2 && results.length === 0 && (
                <div style={hintStyle}>No matters found for “{query}”.</div>
              )}
              {results.map((m) => {
                const ref = m.matter_reference || m.matter_ref;
                const isSel = selected && (selected.matter_reference || selected.matter_ref) === ref;
                return (
                  <button
                    key={ref}
                    data-testid={`link-aml-result-${ref}`}
                    onClick={() => setSelected(m)}
                    style={{
                      ...resultRowStyle,
                      background: isSel ? "rgba(26, 171, 110, 0.18)" : "transparent",
                      borderColor: isSel ? "var(--green)" : "var(--navy-border)",
                    }}
                  >
                    <div style={{ fontWeight: 700, color: "var(--text)" }}>{ref}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                      {m.client_name || m.matter_title || "(no client name)"}
                    </div>
                  </button>
                );
              })}
            </div>

            {err && <div data-testid="link-aml-error" style={errStyle}>{err}</div>}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <button onClick={onClose} style={cancelBtnStyle}>Cancel</button>
              <button
                data-testid="link-aml-confirm"
                onClick={() => handleLink(false)}
                disabled={!selected || busy}
                style={{
                  ...confirmBtnStyle,
                  background: selected ? "var(--green)" : "#374151",
                  cursor: selected && !busy ? "pointer" : "not-allowed",
                }}
              >
                {busy ? "Linking…" : "Link AML"}
              </button>
            </div>
          </>
        )}

        {success && (
          <div data-testid="link-aml-success" style={{ marginTop: 12 }}>
            {/* A refusal is a real outcome and is shown as one. The link was
                still recorded, so the operator can see the pair of matters
                they asked about rather than pressing the button forever. */}
            <div
              style={{ color: success.inherited ? "var(--green)" : "#f59e0b", fontWeight: 700 }}
              data-testid={
                success.inherited ? "link-aml-success-inherited" : "link-aml-success-refused"
              }
            >
              {success.inherited
                ? `AML inherited — ${currentRefAtMount.current} → ${success.linked}`
                : `Nothing inherited from ${success.linked}`}
            </div>

            {!success.inherited && success.reason && (
              <div style={warningBoxStyle} data-testid="link-aml-refusal-reason">
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{success.reason}</div>
              </div>
            )}

            {/* BM-H504 — the human decision the refusal message promises.
                Offered ONLY when the backend says this refusal is the
                genuine name near miss. An expired check, a source with no
                named individual and an AML that was never completed are
                never overridable and get no control here. */}
            {!success.inherited && success.overrideAvailable && !overrideOpen && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>
                  If you know these are the same person written two different
                  ways, a director or the MLCO can confirm that and use the
                  existing check. It is recorded on the file with their name
                  and their reason.
                </div>
                <button
                  data-testid="link-aml-override-open"
                  onClick={() => setOverrideOpen(true)}
                  style={overrideOpenBtnStyle}
                >
                  Same person? Confirm identity and use this AML
                </button>
              </div>
            )}

            {!success.inherited && success.overrideAvailable && overrideOpen && (
              <div style={overrideBoxStyle} data-testid="link-aml-override-panel">
                <div style={{ fontWeight: 700, color: "#fbbf24", fontSize: 12 }}>
                  Confirm this is the same natural person
                </div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                  You are overriding an identity check, not correcting a typo.
                  The AML on <strong>{success.linked}</strong> was performed on{" "}
                  <strong>{success.sourceSubject || "the person named on that matter"}</strong>.
                  This matter is recorded against{" "}
                  <strong>{success.targetSubject || "a different name"}</strong>.
                  Your name, the time, the mismatch you overrode and your reason
                  are written to this matter's audit record.
                </div>

                <label style={{ ...labelStyle, marginTop: 8, display: "flex", gap: 6, alignItems: "flex-start" }}>
                  <input
                    type="checkbox"
                    data-testid="link-aml-override-confirm-checkbox"
                    checked={overrideConfirmed}
                    onChange={(e) => setOverrideConfirmed(e.target.checked)}
                  />
                  <span data-testid="link-aml-override-confirm-label">
                    I confirm {success.sourceSubject || "the source subject"} and{" "}
                    {success.targetSubject || "this matter's client"} are the same
                    natural person.
                  </span>
                </label>

                <label style={{ ...labelStyle, marginTop: 8 }} htmlFor="link-aml-override-reason">
                  Why are you satisfied of that? (at least 20 characters, recorded
                  on the file)
                </label>
                <textarea
                  id="link-aml-override-reason"
                  data-testid="link-aml-override-reason"
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  rows={3}
                  placeholder="e.g. Same client — full name on the passport used for the Thirdfort check, known to the firm as Ben. Verified against the ID on file."
                  style={{ ...inputStyle, resize: "vertical" }}
                />

                {overrideErr && (
                  <div data-testid="link-aml-override-error" style={errStyle}>{overrideErr}</div>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
                  <button
                    data-testid="link-aml-override-cancel"
                    onClick={() => {
                      setOverrideOpen(false);
                      setOverrideConfirmed(false);
                      setOverrideReason("");
                      setOverrideErr(null);
                    }}
                    style={cancelBtnStyle}
                  >
                    Cancel
                  </button>
                  <button
                    data-testid="link-aml-override-submit"
                    onClick={() => handleLink(true)}
                    disabled={!canSubmitOverride}
                    style={{
                      ...confirmBtnStyle,
                      background: canSubmitOverride ? "#b45309" : "#374151",
                      cursor: canSubmitOverride ? "pointer" : "not-allowed",
                    }}
                  >
                    {busy ? "Confirming…" : "Confirm same person and use this AML"}
                  </button>
                </div>
              </div>
            )}

            {success.inherited && success.overridden && (
              <div style={overrideBoxStyle} data-testid="link-aml-override-applied">
                <div style={{ fontWeight: 700, color: "#fbbf24", fontSize: 12 }}>
                  Inherited on a confirmed identity, not on an automatic match
                </div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                  The system did not match these two names.
                  {success.overriddenBy ? ` ${success.overriddenBy}` : " A confirmer"}{" "}
                  confirmed they are the same natural person, and that decision,
                  with the reason given, is on this matter's audit record.
                </div>
              </div>
            )}

            {success.inherited && (
              <>
                {success.completedAt && (
                  <div style={{ marginTop: 6, fontSize: 12, color: "var(--text-muted)" }}>
                    Originally completed: {success.completedAt}
                    {success.stale ? " (outside the usual validity window)" : ""}
                  </div>
                )}
                {success.expiresAt && (
                  <div style={{ marginTop: 4, fontSize: 12, color: "var(--text-muted)" }}>
                    Valid until: {success.expiresAt}
                  </div>
                )}
                {success.reportStatus === "copied" && (
                  <div style={{ marginTop: 4, fontSize: 12, color: "var(--text-muted)" }}>
                    Thirdfort report copied into this matter
                    {success.filesCopied.length ? ` (${success.filesCopied.length} file(s))` : ""}
                  </div>
                )}
                {success.reportStatus === "failed" && (
                  <div style={warningBoxStyle} data-testid="link-aml-report-copy-failed">
                    <div style={{ fontWeight: 700, color: "#fbbf24" }}>
                      Thirdfort report did not copy across
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                      {success.reportError || "No reason returned."} The AML result is
                      recorded, but this matter still points at the source matter's copy
                      of the report and is not yet a self-contained compliance record.
                    </div>
                  </div>
                )}
              </>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              {/* BM-H670: AML now satisfied, so the next step is the FORF.
                  Offer it here rather than send the user back to the board. */}
              {success.inherited && onGoToForf && (
                <button
                  onClick={() => { onClose(); onGoToForf(); }}
                  style={confirmBtnStyle}
                  data-testid="link-aml-go-to-forf"
                >
                  Go to FORF &amp; Risk Assessment
                </button>
              )}
              <button onClick={onClose} style={confirmBtnStyle}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Button (action-row entry point) ─────────────────────────────────────────
export function LinkAMLButton({ matterRef, linkedRef, onSuccess, onGoToForf }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const handleUnlink = async () => {
    if (!window.confirm(
      `Unlink the AML on ${matterRef} from ${linkedRef}? The inherited AML `
      + `result and report stay in place — this only clears the record of `
      + `where they came from.`,
    )) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await axios.post(
        `${API}/matters/${encodeURIComponent(matterRef)}/unlink-aml`,
        {},
        { headers: getHeaders() },
      );
      if (res.data?.matter_reference !== matterRef) {
        setErr(
          `Refusing — server returned matter_reference=${res.data?.matter_reference} `
          + `but we asked to unlink ${matterRef}.`,
        );
        return;
      }
      if (onSuccess) onSuccess(matterRef);
    } catch (e) {
      setErr(errorMessage(e, "Unlink AML failed"));
    } finally {
      setBusy(false);
    }
  };

  // A link inherits whatever the source matter held at the moment it was
  // made. When the source matter's Thirdfort report is approved later,
  // the link has to run again to carry it across. Idempotent: the backend
  // refuses to overwrite an AML result of this matter's own.
  const handleRelink = async () => {
    if (!window.confirm(
      `Re-run the AML link from ${matterRef} to ${linkedRef}? This pulls across `
      + `any identity evidence added to ${linkedRef} since the link was made. `
      + `Nothing already on ${matterRef} is overwritten.`,
    )) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await axios.post(
        `${API}/matters/${encodeURIComponent(matterRef)}/link-aml`,
        { linked_matter_reference: linkedRef },
        { headers: getHeaders() },
      );
      if (res.data?.matter_reference && res.data.matter_reference !== matterRef) {
        setErr(
          `Refusing. Server returned matter_reference=${res.data?.matter_reference} `
          + `but we asked to re-link ${matterRef}.`,
        );
        return;
      }
      if (onSuccess) onSuccess(matterRef);
    } catch (e) {
      setErr(errorMessage(e, "Re-run of the AML link failed"));
    } finally {
      setBusy(false);
    }
  };

  const control = linkedRef ? (
      <span
        style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        data-testid="link-aml-linked-pill"
      >
        <span style={pillStyle}>Linked AML → {linkedRef}</span>
        <button
          data-testid="relink-aml-button"
          onClick={(e) => { e.stopPropagation(); handleRelink(); }}
          disabled={busy}
          style={ghostBtnStyle}
          title="Re-run the AML link and pull across identity evidence added to the linked matter since"
        >
          {busy ? "Working…" : "Refresh AML"}
        </button>
        <button
          data-testid="unlink-aml-button"
          onClick={(e) => { e.stopPropagation(); handleUnlink(); }}
          disabled={busy}
          style={ghostBtnStyle}
          title="Unlink AML — the inherited result and report stay in place"
        >
          {busy ? "Unlinking…" : "Unlink AML"}
        </button>
        {err && <span style={{ color: "var(--red)", fontSize: 11 }}>{err}</span>}
      </span>
  ) : (
      <button
        data-testid="link-aml-button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        style={brandBtnStyle}
        title="Link AML — inherit a completed identity check from another matter for this client"
      >
        🔗 Link AML
      </button>
  );

  // BM-H417. The dialog is rendered here, outside the linked/unlinked
  // choice above, and it is closed by the operator rather than by its own
  // result.
  //
  // It used to be closed twice over. onLinked called setOpen(false) in the
  // same commit that built the outcome panel, and the dialog only existed
  // inside the unlinked branch, so a link that worked flipped linkedRef in
  // the parent and tore the dialog down as well. Either one on its own is
  // enough to make the control silent.
  //
  // The refusal case is the one that matters. When the backend declines to
  // inherit, nothing on the page changes, so the panel is the only thing
  // that can tell the operator why. Closing it leaves them pressing a
  // button that appears to do nothing, which is what happened across five
  // matters on 8 September.
  return (
    <>
      {control}
      {open && (
        <LinkAMLModal
          matterRef={matterRef}
          onClose={() => setOpen(false)}
          onLinked={() => {
            if (onSuccess) onSuccess(matterRef);
          }}
          onGoToForf={onGoToForf}
        />
      )}
    </>
  );
}

// ─── styles ──────────────────────────────────────────────────────────────────
const overlayStyle = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
  display: "flex", alignItems: "center", justifyContent: "center",
  zIndex: 1000,
};
const modalStyle = {
  background: "var(--navy)", color: "var(--text)",
  border: "1px solid var(--navy-border)", borderRadius: 8,
  padding: 16, width: "min(540px, 92vw)", maxHeight: "85vh", overflow: "auto",
  boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
};
const labelStyle = {
  display: "block", fontSize: 11, fontWeight: 600,
  color: "var(--text-muted)", marginBottom: 4,
};
const inputStyle = {
  width: "100%", padding: "8px 10px", fontSize: 13,
  background: "var(--navy-deep)", color: "var(--text)",
  border: "1px solid var(--navy-border)", borderRadius: 4,
  outline: "none", boxSizing: "border-box",
};
const resultListStyle = {
  marginTop: 8, maxHeight: 240, overflowY: "auto",
  display: "flex", flexDirection: "column", gap: 4,
};
const resultRowStyle = {
  textAlign: "left", padding: "8px 10px", borderRadius: 4,
  border: "1px solid var(--navy-border)", cursor: "pointer",
  color: "var(--text)",
};
const hintStyle = {
  fontSize: 11, color: "var(--text-muted)", padding: "8px 0",
};
const errStyle = {
  marginTop: 8, padding: "6px 10px", borderRadius: 4,
  background: "rgba(239, 68, 68, 0.12)", border: "1px solid #ef4444",
  color: "#fca5a5", fontSize: 12,
};
// BM-H504 — the identity-confirmation step is styled apart from the
// ordinary controls on purpose. It is not a retry, it is a decision.
const overrideBoxStyle = {
  marginTop: 10, padding: "10px 12px", borderRadius: 4,
  background: "rgba(180, 83, 9, 0.12)", border: "1px solid #b45309",
};
const overrideOpenBtnStyle = {
  background: "transparent", border: "1px solid #b45309", borderRadius: 4,
  color: "#fbbf24", fontSize: 11, fontWeight: 700, padding: "5px 10px",
  cursor: "pointer",
};
const warningBoxStyle = {
  marginTop: 10, padding: "8px 10px", borderRadius: 4,
  background: "rgba(245, 158, 11, 0.10)", border: "1px solid #f59e0b",
};
const pillStyle = {
  padding: "3px 8px", borderRadius: 999, fontSize: 11,
  background: "rgba(26, 171, 110, 0.14)", color: "var(--green)",
  border: "1px solid var(--green)", whiteSpace: "nowrap",
};
const brandBtnStyle = {
  background: "var(--brand-dim)", border: "1px solid var(--brand-line)",
  borderRadius: 4, color: "var(--brand-text)", fontSize: 11, fontWeight: 700,
  padding: "3px 8px", cursor: "pointer", whiteSpace: "nowrap",
};
const ghostBtnStyle = {
  background: "transparent", border: "1px solid #374151", borderRadius: 4,
  color: "var(--text-muted)", fontSize: 11, padding: "3px 8px",
  cursor: "pointer",
};
const cancelBtnStyle = {
  background: "transparent", border: "1px solid #374151", borderRadius: 4,
  color: "var(--text-muted)", fontSize: 12, padding: "6px 12px",
  cursor: "pointer",
};
const confirmBtnStyle = {
  border: "none", borderRadius: 4, color: "#fff",
  fontSize: 12, fontWeight: 700, padding: "6px 14px",
  cursor: "pointer", background: "var(--green)",
};
const iconBtnStyle = {
  background: "transparent", border: "none", color: "var(--text-muted)",
  fontSize: 22, lineHeight: 1, cursor: "pointer",
};


// ── BM-H577: Refresh AML inheritance ────────────────────────────────────
//
// 21 September 2026 (Vanessa, UAT). BL.VEC.002.1532 inherited its onboarding
// from BL.RM.001.1124 while that matter's Thirdfort report was still pending,
// so the link recorded the only honest answer available at the time: there is
// nothing to inherit. The report on BL.RM.001.1124 was then approved. The
// target matter went on displaying the old refusal, which by then said
// something untrue about a record anyone could open and read for themselves.
//
// The approval now re-runs the inheritance by itself. This button is the same
// operation on demand, for a matter already sitting on a stale refusal. It is
// shown only when the backend has confirmed, against the source record, that
// the AML there IS now satisfied, so pressing it is never a guess.
export function RefreshAMLInheritanceButton({ matterRef, sourceRef, onSuccess }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const handleRefresh = async (e) => {
    e.stopPropagation();
    setBusy(true);
    setErr(null);
    try {
      const res = await axios.post(
        `${API}/matters/${encodeURIComponent(matterRef)}/refresh-aml-inheritance`,
        {},
        { headers: getHeaders() },
      );
      if (res.data?.matter_reference && res.data.matter_reference !== matterRef) {
        setErr(
          `Refusing. Server returned matter_reference=${res.data?.matter_reference} `
          + `but we asked to refresh ${matterRef}.`,
        );
        return;
      }
      if (!res.data?.aml_inherited && !res.data?.aml_satisfied) {
        setErr(res.data?.aml_inherit_skip_reason || "AML still could not be carried across");
        return;
      }
      if (onSuccess) onSuccess(matterRef);
    } catch (e2) {
      setErr(errorMessage(e2, "Refresh of the AML inheritance failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <button
        onClick={handleRefresh}
        disabled={busy}
        data-testid={`refresh-aml-inheritance-${matterRef}`}
        title={
          sourceRef
            ? `Carry the completed AML on ${sourceRef} across to ${matterRef}`
            : `Re-check the AML on the matter ${matterRef} is linked to`
        }
        style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          background: "transparent", border: "1px solid var(--brand-line)",
          borderRadius: 4, color: "var(--brand-text)",
          fontSize: 10, fontWeight: 600, padding: "3px 8px",
          cursor: busy ? "default" : "pointer",
        }}
      >
        {busy ? "Refreshing…" : "Refresh AML inheritance"}
      </button>
      {err && (
        <span
          style={{ fontSize: 10, color: "#f59e0b" }}
          data-testid={`refresh-aml-inheritance-error-${matterRef}`}
        >
          {err}
        </span>
      )}
    </span>
  );
}
